import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StoreIcon from '@mui/icons-material/Store';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ContactsIcon from '@mui/icons-material/Contacts';
import BadgeIcon from '@mui/icons-material/Badge';
import StarIcon from '@mui/icons-material/Star';
import axiosInstance from '../../lib/axios';
import { GoogleMapsWrapper, MapMarker } from '../../components/GoogleMapsWrapper';
import type { Reservation } from '../../types';
import {
  Customer,
  CustomerContact,
  getCustomerDisplayName,
  isBusinessCustomer,
  getContactFullName,
} from '../../types/customer';
import { palette } from '../../theme/colors';

// Extended Customer type with reservations for show page
type CustomerWithDetails = Customer & {
  reservations?: Reservation[];
};

const fetchCustomer = async (id: string): Promise<CustomerWithDetails> => {
  const response = await axiosInstance.get(`/customers/${id}`);
  return response.data;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'confirmed':
      return 'info';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const getTierColor = (tierName: string) => {
  switch (tierName.toLowerCase()) {
    case 'platinum':
      return '#E5E4E2';
    case 'gold':
      return '#FFD700';
    case 'silver':
      return '#C0C0C0';
    case 'bronze':
      return '#CD7F32';
    default:
      return palette.info;
  }
};

export const CustomerShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => fetchCustomer(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/customers')}>
            <ArrowBackIcon />
          </IconButton>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/customers')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Client non trouvé</Typography>
        </Box>
        <Card>
          <CardContent>
            <Typography color="error">
              Impossible de charger les détails du client. Le client n'existe peut-être pas ou vous n'avez pas la permission de le consulter.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/app/customers')}
              sx={{ mt: 2 }}
            >
              Retour aux clients
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const isBusiness = isBusinessCustomer(customer);
  const displayName = getCustomerDisplayName(customer);

  // Prepare map marker if customer has location
  const mapMarkers: MapMarker[] = customer.latitude && customer.longitude
    ? [{
        id: customer.id,
        position: { lat: customer.latitude, lng: customer.longitude },
        title: displayName,
        icon: 'customer',
        info: (
          <Box>
            <Typography variant="subtitle2">{displayName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[customer.city, customer.state].filter(Boolean).join(', ')}
            </Typography>
          </Box>
        ),
      }]
    : [];

  const fullAddress = [
    customer.street,
    customer.city,
    customer.state,
    customer.postalCode,
    customer.country,
  ].filter(Boolean).join(', ');

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/app/customers')}>
            <ArrowBackIcon />
          </IconButton>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                bgcolor: isBusiness ? palette.info : palette.primary,
                width: 56,
                height: 56,
                fontSize: '1.5rem',
              }}
            >
              {isBusiness
                ? (customer.companyName?.[0]?.toUpperCase() || <BusinessIcon />)
                : (customer.firstName?.[0]?.toUpperCase() || <PersonIcon />)
              }
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4">
                  {displayName}
                </Typography>
                <Chip
                  icon={isBusiness ? <BusinessIcon /> : <PersonIcon />}
                  label={isBusiness ? 'Entreprise' : 'Particulier'}
                  color={isBusiness ? 'info' : 'default'}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Client depuis le {new Date(customer.createdAt).toLocaleDateString('fr-FR')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {isBusiness && (
            <Chip
              icon={customer.isApproved ? <CheckCircleIcon /> : <BlockIcon />}
              label={customer.isApproved ? 'Approuvé' : 'Non approuvé'}
              color={customer.isApproved ? 'success' : 'error'}
              variant="filled"
            />
          )}
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate(`/app/customers/${id}/history`)}
          >
            Voir l'historique
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Customer Info */}
        <Grid item xs={12} md={6}>
          {/* Business-specific: Company Information */}
          {isBusiness && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informations de l'entreprise
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BusinessIcon sx={{ color: palette.textMuted }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Nom de l'entreprise
                      </Typography>
                      <Typography>{customer.companyName}</Typography>
                    </Box>
                  </Box>

                  {/* Pricing Tier */}
                  {customer.defaultTier ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <LocalOfferIcon sx={{ color: getTierColor(customer.defaultTier.tierName) }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Niveau de tarification
                        </Typography>
                        <Typography sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                          {customer.defaultTier.tierName}
                          {' - '}
                          {customer.defaultTier.discountType === 'percentage'
                            ? `${customer.defaultTier.discountValue}%`
                            : formatCurrency(customer.defaultTier.discountValue)}
                          {' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            de réduction
                          </Typography>
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <LocalOfferIcon sx={{ color: palette.textMuted }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Niveau de tarification
                        </Typography>
                        <Typography color="text.secondary">
                          Aucun niveau assigné (tarifs par défaut)
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Approval Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {customer.isApproved ? (
                      <CheckCircleIcon sx={{ color: palette.success }} />
                    ) : (
                      <BlockIcon sx={{ color: palette.error }} />
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Statut d'approbation
                      </Typography>
                      <Typography sx={{ color: customer.isApproved ? palette.success : palette.error }}>
                        {customer.isApproved ? 'Approuvé - Accès B2B autorisé' : 'Non approuvé - Accès B2B révoqué'}
                      </Typography>
                    </Box>
                  </Box>

                  {customer.approvedBy && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PersonIcon sx={{ color: palette.textMuted }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Approuvé par
                        </Typography>
                        <Typography>
                          {customer.approvedBy.firstName} {customer.approvedBy.lastName}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {customer.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: palette.surfaceElevated }}
                    >
                      <Typography variant="body2">{customer.notes}</Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Individual-specific: Personal Information */}
          {!isBusiness && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informations personnelles
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonIcon sx={{ color: palette.textMuted }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Nom complet
                      </Typography>
                      <Typography>
                        {customer.firstName} {customer.lastName}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {customer.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: palette.surfaceElevated }}
                    >
                      <Typography variant="body2">{customer.notes}</Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contact Information (common for both types) */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Coordonnées
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ color: palette.textMuted }} />
                  <Typography>{customer.email}</Typography>
                </Box>
                {customer.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ color: palette.textMuted }} />
                    <Typography>{customer.phone}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Business-specific: Contacts Section */}
          {isBusiness && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ContactsIcon sx={{ color: palette.textMuted }} />
                  <Typography variant="h6">
                    Contacts de l'entreprise
                  </Typography>
                  <Chip
                    label={customer.contacts?.length || 0}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                {customer.contacts && customer.contacts.length > 0 ? (
                  <List disablePadding>
                    {customer.contacts.map((contact: CustomerContact, index: number) => (
                      <Box key={contact.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          alignItems="flex-start"
                          sx={{ px: 0 }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: contact.isPrimary ? palette.primary : palette.surfaceElevated,
                                color: contact.isPrimary ? '#fff' : palette.textMuted,
                              }}
                            >
                              {contact.firstName?.[0]?.toUpperCase() || <PersonIcon />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography fontWeight={500}>
                                  {getContactFullName(contact)}
                                </Typography>
                                {contact.isPrimary && (
                                  <Chip
                                    icon={<StarIcon sx={{ fontSize: 12 }} />}
                                    label="Principal"
                                    size="small"
                                    color="primary"
                                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {contact.role && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <BadgeIcon sx={{ fontSize: 14, color: palette.textMuted }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {contact.role}
                                    </Typography>
                                  </Box>
                                )}
                                {contact.email && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <EmailIcon sx={{ fontSize: 14, color: palette.textMuted }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {contact.email}
                                    </Typography>
                                  </Box>
                                )}
                                {contact.phone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PhoneIcon sx={{ fontSize: 14, color: palette.textMuted }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {contact.phone}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      py: 3,
                      textAlign: 'center',
                      bgcolor: palette.surfaceElevated,
                      borderRadius: 2,
                    }}
                  >
                    <ContactsIcon sx={{ fontSize: 40, color: palette.textMuted, mb: 1 }} />
                    <Typography color="text.secondary">
                      Aucun contact enregistré
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ajoutez des contacts via le formulaire de modification
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Address */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Adresse par défaut
              </Typography>
              {fullAddress ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <LocationOnIcon sx={{ color: palette.textMuted, mt: 0.5 }} />
                  <Box>
                    <Typography>{fullAddress}</Typography>
                    {customer.latitude && customer.longitude && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: 14, color: palette.success }} />
                        GPS: {customer.latitude.toFixed(4)}, {customer.longitude.toFixed(4)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">Aucune adresse fournie</Typography>
              )}
            </CardContent>
          </Card>

          {/* Delivery Preferences */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Préférences de livraison
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {customer.preferDelivery && (
                  <Chip
                    icon={<LocalShippingIcon />}
                    label="Préfère la livraison"
                    color="primary"
                  />
                )}
                {customer.preferPickup && (
                  <Chip
                    icon={<StoreIcon />}
                    label="Préfère le retrait"
                    color="secondary"
                  />
                )}
                {!customer.preferDelivery && !customer.preferPickup && (
                  <Typography color="text.secondary">Aucune préférence définie</Typography>
                )}
              </Box>
              {customer.deliveryInstructions && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Instructions de livraison
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: palette.surfaceElevated }}
                  >
                    <Typography variant="body2">{customer.deliveryInstructions}</Typography>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Map and Reservations */}
        <Grid item xs={12} md={6}>
          {/* Activity Stats for Business Customers */}
          {isBusiness && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Statistiques
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: palette.surfaceElevated,
                    }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 32, color: palette.primary, mb: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {customer.totalOrders || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Commandes totales
                    </Typography>
                  </Paper>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: palette.surfaceElevated,
                    }}
                  >
                    <LocalOfferIcon sx={{ fontSize: 32, color: palette.success, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(customer.totalOrderValue || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Valeur totale
                    </Typography>
                  </Paper>
                </Box>
                {customer.lastActivityAt && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Dernière activité: {new Date(customer.lastActivityAt).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {customer.lastActivityType && ` (${customer.lastActivityType.replace('_', ' ')})`}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Map */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Localisation
              </Typography>
              {customer.latitude && customer.longitude ? (
                <GoogleMapsWrapper
                  markers={mapMarkers}
                  center={{ lat: customer.latitude, lng: customer.longitude }}
                  zoom={14}
                  height={250}
                  darkMode={true}
                  fitBounds={false}
                />
              ) : (
                <Box
                  sx={{
                    height: 250,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: palette.surfaceElevated,
                    borderRadius: 2,
                    border: `1px dashed ${palette.border}`,
                  }}
                >
                  <Box textAlign="center">
                    <LocationOnIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                    <Typography color="text.secondary">
                      Aucune donnée de localisation disponible
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Le client n'a pas fourni de coordonnées GPS
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Reservation History */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">
                  Historique des réservations
                </Typography>
                <Chip
                  icon={<ShoppingCartIcon />}
                  label={`${customer.reservations?.length || 0} commandes`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              {customer.reservations && customer.reservations.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Articles</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customer.reservations.slice(0, 10).map((reservation) => (
                        <TableRow key={reservation.id} hover>
                          <TableCell>
                            {new Date(reservation.createdAt).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={reservation.status}
                              size="small"
                              color={getStatusColor(reservation.status) as any}
                            />
                          </TableCell>
                          <TableCell>
                            {reservation.items?.length || 0} articles
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(reservation.totalAmount)}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/app/reservations/${reservation.id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    bgcolor: palette.surfaceElevated,
                    borderRadius: 2,
                  }}
                >
                  <ShoppingCartIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                  <Typography color="text.secondary">
                    Aucune réservation pour le moment
                  </Typography>
                </Box>
              )}
              {customer.reservations && customer.reservations.length > 10 && (
                <Box textAlign="center" mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Affichage de 10 sur {customer.reservations.length} réservations
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
